/**
 * @deprecated This dialog component is no longer used.
 * Use the full-page route /leads/new for creating leads instead.
 * Navigate to '/leads/new' for lead creation.
 */

import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCreateLead, useConvertLead } from '@/hooks/useLeads';
import { useTechs } from '@/hooks/useReferenceData';
import { createLeadFormSchema, type LeadFormData } from '@/schemas/lead.schema';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LeadForm, type LinkedCustomer } from './LeadForm';
import { EstimateFromLeadDialog } from '@/components/estimates/EstimateFromLeadDialog';
import { Save, Wrench, FileText } from 'lucide-react';

interface CreateLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-filled values, e.g. when raising a lead for an existing customer */
  prefill?: Partial<LeadFormData>;
  /** Link the new lead to this customer record */
  customerId?: string | null;
  /** Fired after the lead row exists (before any estimate/job hand-off) */
  onLeadCreated?: (leadId: string) => void;
}

export function CreateLeadDialog({ open, onOpenChange, prefill, customerId, onLeadCreated }: CreateLeadDialogProps) {
  const [estimateLead, setEstimateLead] = useState<{
    id: string;
    customer_name: string;
    customer_phone: string | null;
    customer_email: string | null;
    customer_address: string | null;
    issue_description: string;
    quoted_price: number | null;
    customer_id: string | null;
  } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const createLead = useCreateLead();
  const convertLead = useConvertLead();
  const { data: techs } = useTechs();

  const [selectedAction, setSelectedAction] = useState<'save' | 'convert' | 'estimate' | null>(null);
  const [selectedTech, setSelectedTech] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // A caller that opens this dialog for a known customer (the customer card's
  // New Estimate flow) passes customerId; the picker inside the form can still
  // change or clear it. Whatever is linked at save time is what gets stored.
  const [linkedCustomer, setLinkedCustomer] = useState<LinkedCustomer | null>(() =>
    customerId
      ? {
          id: customerId,
          customer_name: prefill?.customer_name ?? 'Selected customer',
          primary_phone: prefill?.customer_phone ?? '',
          company_name: prefill?.company_name ?? null,
        }
      : null,
  );
  const effectiveCustomerId = linkedCustomer?.id ?? null;

  const { locale } = useCompanyLocale();
  const leadSchema = useMemo(() => createLeadFormSchema(locale), [locale]);

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      issue_description: '',
      lead_source_id: '',
      campaign_id: '',
      notes: '',
      ...prefill,
    },
  });

  const handleAction = async (action: 'save' | 'convert' | 'estimate') => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (action === 'convert' && !selectedTech) {
      setSelectedAction('convert');
      return;
    }

    const data = form.getValues();
    if (!user) return;

    setIsProcessing(true);

    if (action === 'save') {
      createLead.mutate(
        { data, userId: user.id, customerId: effectiveCustomerId },
        {
          onSuccess: (created) => {
            onLeadCreated?.(created.id);
            form.reset();
            setSelectedAction(null);
            setSelectedTech('');
            onOpenChange(false);
          },
          onSettled: () => setIsProcessing(false),
        }
      );
    } else if (action === 'convert') {
      createLead.mutate(
        { data, userId: user.id, customerId: effectiveCustomerId },
        {
          onSuccess: (created) => {
            onLeadCreated?.(created.id);
            Promise.resolve({ data: created })
              .then(({ data: lead }) => {
                if (lead) {
                  convertLead.mutate(
                    { leadId: lead.id, techId: selectedTech },
                    {
                      onSuccess: () => {
                        form.reset();
                        setSelectedAction(null);
                        setSelectedTech('');
                        onOpenChange(false);
                        navigate('/jobs');
                      },
                      onSettled: () => setIsProcessing(false),
                    }
                  );
                }
              });
          },
          onError: () => setIsProcessing(false),
        }
      );
    } else if (action === 'estimate') {
      // Every estimate hangs off a lead: create the lead first, then open the
      // estimate form for it. Nothing here can save a quote without a lead.
      createLead.mutate(
        { data, userId: user.id, customerId: effectiveCustomerId },
        {
          onSuccess: (created) => {
            onLeadCreated?.(created.id);
            setEstimateLead({
              id: created.id,
              customer_name: data.customer_name,
              customer_phone: data.customer_phone || null,
              customer_email: data.customer_email || null,
              customer_address: data.customer_address || null,
              issue_description: data.issue_description,
              quoted_price: data.quoted_price ? parseFloat(data.quoted_price) : null,
              customer_id: effectiveCustomerId,
            });
            form.reset();
            setSelectedAction(null);
            onOpenChange(false);
          },
          onSettled: () => setIsProcessing(false),
        }
      );
    }
  };

  return (
    <>
    {estimateLead && (
      <EstimateFromLeadDialog
        open={!!estimateLead}
        onOpenChange={(o) => { if (!o) setEstimateLead(null); }}
        lead={estimateLead}
      />
    )}
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Lead</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4">
            <LeadForm form={form} linkedCustomer={linkedCustomer} onLinkCustomer={setLinkedCustomer} />

            {selectedAction === 'convert' && (
              <div className="p-4 border rounded-md bg-muted/30">
                <Label>Select Technician *</Label>
                <Select value={selectedTech} onValueChange={setSelectedTech}>
                  <SelectTrigger className="mt-2 bg-background">
                    <SelectValue placeholder="Choose a technician" />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    {techs?.map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>
                        {tech.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-between items-center gap-2 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  onOpenChange(false);
                  setSelectedAction(null);
                  setSelectedTech('');
                }}
              >
                Cancel
              </Button>
              
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => handleAction('save')}
                  disabled={isProcessing}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Create Lead
                </Button>
                
                <Button 
                  type="button"
                  onClick={() => handleAction('convert')}
                  disabled={isProcessing || (selectedAction === 'convert' && !selectedTech)}
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  {selectedAction === 'convert' && !selectedTech ? 'Select Tech' : 'Book Job'}
                </Button>
                
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => handleAction('estimate')}
                  disabled={isProcessing}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Create Estimate
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    </>
  );
}
